import React, { useState } from 'react';
import {
  Trash, Eye, EyeSlash, PencilSimple, Check, X,
  CopySimple, LockSimple, LockSimpleOpen, Palette
} from '@phosphor-icons/react';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { COLORS } from './mapUtils';

function IBtn({ onClick, title, children, cls = '' }) {
  return <button onClick={onClick} title={title} className={`w-6 h-6 flex items-center justify-center rounded hover:bg-gray-100 dark:hover:bg-white/10 opacity-50 hover:opacity-100 ${cls}`}>{children}</button>;
}

export default function MeasurementPanel({
  panelCls, measurements,
  onToggleVis, onShowAll, onHideAll,
  onStartDelete, onDuplicate, onToggleLock,
  onUpdateColor, onUpdateOpacity,
  onSaveEdit, onClose,
  showSiteLogisticsTab = true,
}) {
  const [editingIdx, setEditingIdx] = useState(null);
  const [editTitle, setEditTitle] = useState('');
  const [editDesc, setEditDesc] = useState('');
  const [showColorPicker, setShowColorPicker] = useState(null);
  const [activeTab, setActiveTab] = useState('measurements');

  // If the user lost access to Site Logistics (plan downgrade), fall back to the
  // measurements tab automatically so we don't render an empty filtered list.
  if (!showSiteLogisticsTab && activeTab === 'site') {
    setActiveTab('measurements');
  }

  function startEdit(i) { setEditingIdx(i); setEditTitle(measurements[i]?.label || ''); setEditDesc(measurements[i]?.description || ''); }
  function saveEdit() {
    if (editingIdx === null) return;
    onSaveEdit(editingIdx, editTitle, editDesc);
    setEditingIdx(null);
  }
  function cancelEdit() { setEditingIdx(null); }

  if (measurements.length === 0) return null;

  // Build a list of [measurement, originalIndex] entries filtered for the active tab.
  // We keep the original index so callbacks (which operate on the full measurements array)
  // continue to work for delete / duplicate / lock / colour etc.
  const filtered = measurements
    .map((m, i) => ({ m, i }))
    .filter(({ m }) => {
      const isSite = m.measurement_type === 'site_logistics' || (m.shape_type || '').startsWith('site_');
      return activeTab === 'site' ? isSite : !isSite;
    });
  const siteCount = measurements.filter(m => m.measurement_type === 'site_logistics' || (m.shape_type || '').startsWith('site_')).length;
  const measureCount = measurements.length - siteCount;

  return (
    <div className={`absolute top-[84px] right-3 w-80 ${panelCls} rounded-md z-10 max-h-[65vh] flex flex-col overflow-hidden`} data-testid="measurements-panel">
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-600">
        <h4 className="text-sm font-bold">Shapes ({measurements.length})</h4>
        <div className="flex items-center gap-3 text-xs font-semibold">
          <button onClick={onShowAll} className="text-primary hover:underline" data-testid="panel-show-all">Show</button>
          <button onClick={onHideAll} className="opacity-50 hover:opacity-100" data-testid="panel-hide-all">Hide</button>
          {onClose && (
            <button onClick={onClose} className="w-5 h-5 flex items-center justify-center rounded hover:bg-gray-100 dark:hover:bg-white/10 opacity-50 hover:opacity-100 ml-1" title="Close panel">
              <X size={14} weight="bold" />
            </button>
          )}
        </div>
      </div>
      {/* Tabs */}
      <div className="flex border-b border-gray-200 dark:border-gray-600 px-2 pt-1 gap-1">
        <button
          onClick={() => setActiveTab('measurements')}
          className={`px-2.5 py-1.5 text-xs font-semibold rounded-t-md transition-colors ${activeTab === 'measurements' ? 'bg-primary/10 text-primary border-b-2 border-primary -mb-px' : 'opacity-60 hover:opacity-100'}`}
          data-testid="tab-measurements"
        >
          Measurements <span className="opacity-60">({measureCount})</span>
        </button>
        {showSiteLogisticsTab && (
          <button
            onClick={() => setActiveTab('site')}
            className={`px-2.5 py-1.5 text-xs font-semibold rounded-t-md transition-colors ${activeTab === 'site' ? 'bg-primary/10 text-primary border-b-2 border-primary -mb-px' : 'opacity-60 hover:opacity-100'}`}
            data-testid="tab-site"
          >
            Site Logistics <span className="opacity-60">({siteCount})</span>
          </button>
        )}
      </div>
      <div className="flex-1 overflow-y-auto">
        {filtered.length === 0 && (
          <div className="px-4 py-10 text-center text-xs opacity-50" data-testid="panel-empty-tab">
            {activeTab === 'site' ? 'No site items yet. Use the Site Logistics tool to place crane, office, storage, toilet, or skip bins.' : 'No measurements yet.'}
          </div>
        )}
        {filtered.map(({ m, i }) => (
          <div key={m.id || i} className="border-b border-gray-100 dark:border-gray-700 last:border-0" data-testid={`measurement-item-${i}`}>
            <div className={`flex border-l-4 ${m.hidden ? 'border-gray-300 dark:border-gray-600 opacity-40' : m.locked ? 'border-warning' : 'border-success'}`}>
              <div className="flex-1 p-3">
                {editingIdx === i ? (
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <span className="w-3 h-3 rounded-sm shrink-0" style={{ backgroundColor: m.color || '#7367F0' }} />
                      <Input value={editTitle} onChange={(e) => setEditTitle(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') saveEdit(); if (e.key === 'Escape') cancelEdit(); }} className="h-7 text-sm font-semibold rounded-sm" autoFocus />
                      <button onClick={saveEdit} className="text-success shrink-0"><Check size={16} weight="bold" /></button>
                      <button onClick={cancelEdit} className="opacity-50 shrink-0"><X size={16} weight="bold" /></button>
                    </div>
                    <Textarea value={editDesc} onChange={(e) => setEditDesc(e.target.value)} placeholder="Add description..." className="text-xs min-h-[40px] rounded-sm resize-y" />
                  </div>
                ) : (
                  <>
                    <div className="flex items-center gap-2 mb-1">
                      <span className="w-3 h-3 rounded-sm shrink-0" style={{ backgroundColor: m.color || '#7367F0' }} />
                      <span className="text-sm font-semibold flex-1 min-w-0 truncate">{m.label}</span>
                      <div className="flex items-center gap-0.5 shrink-0 ml-1">
                        <IBtn onClick={() => onToggleVis(i)} title={m.hidden ? 'Show' : 'Hide'}>{m.hidden ? <EyeSlash size={13} /> : <Eye size={13} />}</IBtn>
                        <IBtn onClick={() => startEdit(i)} title="Edit"><PencilSimple size={13} /></IBtn>
                        <IBtn onClick={() => setShowColorPicker(showColorPicker === i ? null : i)} title="Color"><Palette size={13} /></IBtn>
                        <IBtn onClick={() => onDuplicate(i)} title="Duplicate"><CopySimple size={13} /></IBtn>
                        <IBtn onClick={() => onToggleLock(i)} title={m.locked ? 'Unlock' : 'Lock'}>{m.locked ? <LockSimple size={13} /> : <LockSimpleOpen size={13} />}</IBtn>
                        <IBtn onClick={() => onStartDelete(i)} title="Delete" cls="hover:text-destructive"><Trash size={13} /></IBtn>
                      </div>
                    </div>
                    <div className="text-[11px] opacity-60 whitespace-pre-line ml-5 leading-relaxed">{m.details}</div>
                    {m.description && <p className="text-[11px] italic opacity-50 ml-5 mt-1">"{m.description}"</p>}
                    {showColorPicker === i && (
                      <div className="ml-5 mt-2 flex items-center gap-2 flex-wrap">
                        {COLORS.map(c => (
                          <button key={c} onClick={() => { onUpdateColor(i, c); setShowColorPicker(null); }} className={`w-5 h-5 rounded-sm border-2 ${m.color === c ? 'border-foreground' : 'border-transparent'}`} style={{ backgroundColor: c }} />
                        ))}
                        <input type="range" min="0" max="50" value={(m.opacity || 0.12) * 100} onChange={(e) => onUpdateOpacity(i, e.target.value / 100)} className="w-16 h-1 accent-primary ml-1" title="Opacity" />
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
